import { Activity, WellnessDimension } from '../types/recommendations';
import { GoIconActivityMappingOptions, mapActivitiesResponse } from './goiconActivityMapper';
import { createGoIconApiService } from './goiconApiService';

/**
 * For recurring or duplicated events, keep only the nearest upcoming occurrence per unique title.
 */
function deduplicateByTitle(activities: Activity[]): Activity[] {
  const byTitle = new Map<string, Activity>();

  for (const activity of activities) {
    const key = (activity.title ?? '').trim().toLowerCase();
    if (!key) continue;

    const existing = byTitle.get(key);
    if (!existing) {
      byTitle.set(key, activity);
      continue;
    }

    // Keep whichever occurrence starts sooner
    const existingMs = existing.startTime ? new Date(existing.startTime).getTime() : Infinity;
    const currentMs = activity.startTime ? new Date(activity.startTime).getTime() : Infinity;
    if (currentMs < existingMs) {
      byTitle.set(key, activity);
    }
  }

  return Array.from(byTitle.values());
}

export interface EventsServiceConfig {
  facilityId: string;
  goiconResidentId?: string; // GoIcon internal resident ID
  apiKey: string;
  goiconDimensionIdToWellness?: Record<number, WellnessDimension>;
  /** Source Recommended Activities from GoIcon's personalized smart endpoint instead of facility activities. Default false. */
  useSmartRecommendations?: boolean;
}

export class EventsService {
  private config: EventsServiceConfig;

  constructor(config: EventsServiceConfig) {
    this.config = config;
  }

  private getProxyUrl(): string {
    // Explicit override (same convention as supabaseAuth's exchangeEndpoint):
    // set REACT_APP_FUNCTIONS_BASE= (empty) under `netlify dev` so requests hit
    // the LOCAL functions instead of the deployed ones.
    const explicitBase = process.env.REACT_APP_FUNCTIONS_BASE;
    if (explicitBase !== undefined) {
      return `${explicitBase.replace(/\/$/, '')}/.netlify/functions/goicon-api-proxy`;
    }
    // Plain `npm start` has no local functions - use the deployed proxy to avoid CORS
    if (process.env.NODE_ENV === 'development') {
      return 'https://wellness.goicon.co/.netlify/functions/goicon-api-proxy';
    }
    return '/.netlify/functions/goicon-api-proxy';
  }

  /**
   * Fetch the set of group IDs the resident belongs to.
   * Returns null when goiconResidentId is absent — callers treat null as "no filtering".
   * Returns an empty Set when the API confirms the resident has no groups.
   */
  private async getResidentGroupIds(): Promise<Set<number> | null> {
    if (!this.config.goiconResidentId) return null;

    const goiconService = createGoIconApiService(this.config.apiKey);
    const groups = await goiconService.getResidentGroups(
      this.config.facilityId,
      this.config.goiconResidentId
    );
    const ids = new Set<number>(
      groups.map((g: any) => Number(g.id)).filter(Number.isFinite)
    );
    console.log(`👥 Resident group IDs: [${Array.from(ids).join(', ')}]`);
    return ids;
  }

  /**
   * Fetch upcoming facility activities for the next 7 days (from now).
   * GET /facilities/{facilityId}/activities?i_social=true&i_started_date={now}&i_ended_date={now+7d}
   */
  private async getUpcomingActivitiesRaw(): Promise<any[]> {
    const now = Math.floor(Date.now() / 1000);
    const sevenDaysFromNow = now + 7 * 24 * 60 * 60;

    const qs = new URLSearchParams({
      i_social: 'true',
      i_started_date: String(now),
      i_ended_date: String(sevenDaysFromNow),
    });

    const endpoint = `/facilities/${this.config.facilityId}/activities?${qs.toString()}`;
    const proxyUrl = this.getProxyUrl();
    const url = `${proxyUrl}?endpoint=${encodeURIComponent(endpoint)}`;

    console.log('📅 Fetching upcoming facility activities:', endpoint);

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-GoIcon-API-Key': this.config.apiKey,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Activities API error ${response.status}: ${errorText.slice(0, 300)}`);
    }

    const data = await response.json();
    const list = Array.isArray(data) ? data : (data?.items ?? data?.data ?? data?.activities ?? []);
    console.log(`📅 Received ${list.length} raw activities`);
    return list;
  }

  /**
   * Determine whether an activity is visible to this resident.
   * - null groupIds means no resident context → show all activities.
   * - An activity with NO eventGroups is visible to everyone.
   * - An activity WITH eventGroups is visible only if the resident belongs to at least one of those groups.
   */
  private isVisibleToResident(rawActivity: any, residentGroupIds: Set<number> | null): boolean {
    if (residentGroupIds === null) return true;
    const groups: any[] = rawActivity?.eventGroups ?? [];
    if (groups.length === 0) return true;
    return groups.some((g: any) => {
      // eventGroups[].id is the facility group ID (matches facilityMemberGroups[].id)
      const gid = Number(g?.id);
      return Number.isFinite(gid) && residentGroupIds.has(gid);
    });
  }

  // ─── Smart recommendations ────────────────────────────────────────────────
  // Enabled per-facility via config.useSmartRecommendations (ProgramSettings
  // "Use platform event recommendations" toggle). When active and a
  // goiconResidentId is present, this replaces the facility-activities path
  // with the personalised endpoint:
  //   GET /facilities/{fid}/users/{uid}/smart-resident-dashboard/api/events/recommendations

  private async getSmartRecommendations(): Promise<Activity[]> {
    if (!this.config.goiconResidentId) {
      throw new Error('GoIcon resident ID required for smart recommendations');
    }
    const endpoint = `/facilities/${this.config.facilityId}/users/${this.config.goiconResidentId}/smart-resident-dashboard/api/events/recommendations`;
    const proxyUrl = this.getProxyUrl();
    const url = `${proxyUrl}?endpoint=${encodeURIComponent(endpoint)}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-GoIcon-API-Key': this.config.apiKey,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Smart recommendations API error ${response.status}: ${errorText.slice(0, 300)}`);
    }

    const data = await response.json();
    const opts: GoIconActivityMappingOptions | undefined = this.config.goiconDimensionIdToWellness
      ? { goiconDimensionIdToWellness: this.config.goiconDimensionIdToWellness }
      : undefined;
    return mapActivitiesResponse(data, opts);
  }
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Main entry point.
   * When config.useSmartRecommendations is true (and a goiconResidentId is present),
   * uses the personalised smart recommendations endpoint, falling back to upcoming
   * facility activities if that request fails for any reason (isFallback: true in
   * that case). Otherwise — flag off, or no resident ID — uses upcoming facility
   * activities filtered by the resident's group membership directly.
   * Scoring (deficit / goals) is applied by EventRecommendations after this returns.
   */
  async getEventRecommendationsWithFallback(): Promise<{
    activities: Activity[];
    isFallback: boolean;
    /** Smart endpoint answered successfully but with ZERO activities — meaning the
     *  resident has no interests set, or no upcoming activities match them.
     *  The returned list is the facility-activities fallback. */
    smartNoMatches?: boolean;
  }> {
    if (this.config.useSmartRecommendations && this.config.goiconResidentId) {
      try {
        console.log('🎯 Using smart recommendations path');
        const activities = (await this.getSmartRecommendations())
          .filter((a) => (a.title ?? '').trim().length > 0);
        if (activities.length === 0) {
          console.log('🎯 Smart recommendations returned no activities (no interests set, or none relevant) — using facility activities');
          return { ...(await this.getFacilityActivities()), smartNoMatches: true };
        }
        return { activities, isFallback: false };
      } catch (err) {
        console.error('🎯 Smart recommendations failed, falling back to facility activities:', err);
        return { ...(await this.getFacilityActivities()), isFallback: true };
      }
    }
    return this.getFacilityActivities();
  }

  /**
   * Upcoming facility activities filtered by the resident's group membership,
   * deduplicated by title. The non-smart source for Recommended Activities.
   */
  private async getFacilityActivities(): Promise<{ activities: Activity[]; isFallback: boolean }> {
    const [residentGroupIds, rawActivities] = await Promise.all([
      this.getResidentGroupIds(),
      this.getUpcomingActivitiesRaw(),
    ]);

    const visible = rawActivities.filter((a) => this.isVisibleToResident(a, residentGroupIds));
    console.log(
      `📅 Group filter: ${rawActivities.length} total → ${visible.length} visible to this resident`
    );

    const opts: GoIconActivityMappingOptions | undefined = this.config.goiconDimensionIdToWellness
      ? { goiconDimensionIdToWellness: this.config.goiconDimensionIdToWellness }
      : undefined;

    const mapped = mapActivitiesResponse(visible, opts).filter(
      (a) => (a.title ?? '').trim().length > 0
    );
    console.log(`📅 ${mapped.length} activities after mapping and title filter`);

    const deduped = deduplicateByTitle(mapped);
    console.log(`📅 ${deduped.length} activities after title deduplication`);

    return { activities: deduped, isFallback: false };
  }

  // Legacy compatibility
  async listActivities(_params: { facilityId: string; start?: string; end?: string }): Promise<Activity[]> {
    const { activities } = await this.getEventRecommendationsWithFallback();
    return activities;
  }
}
